import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import 'bootstrap/dist/css/bootstrap.min.css';
import GoldSiverHeader from '../HomePage/GoldSiverHeader';
import axios from '../../api/axios';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

const AdventureLog = () => {
    const { t, i18n } = useTranslation();
    const [logs, setLogs] = useState([]);
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);
    
    // Get user data from Redux
    const userRedux = useSelector(state => state.auth.user_data);

    useEffect(() => {
        fetchLogs();
    }, []);

    const fetchLogs = async () => {
        try {
            setLoading(true);
            const response = await axios.get('/api/api/adventure_log.php');
            if (response.data.success) {
                setLogs(response.data.logs);
                setStats(response.data.stats);
            }
        } catch (error) {
            console.error('Failed to fetch adventure logs', error);
        } finally {
            setLoading(false);
        }
    };

    // Use stats from API if available, otherwise fallback to Redux or 0
    const userStats = {
        caught_count: stats?.pokemon_caught ?? userRedux?.caught_count ?? 0,
        wins: stats?.battles_won ?? userRedux?.wins ?? 0,
        silver: stats?.silver_earned ?? userRedux?.silver ?? 0,
        badges_count: stats?.badges ?? userRedux?.badges_count ?? 0
    };

    const isRtl = i18n.language === 'he';

    const styles = {
        ar_log_section: {
            padding: '50px 0',
            color: 'white',
            direction: isRtl ? 'rtl' : 'ltr'
        },
        ar_log_card: {
            background: 'rgba(0, 0, 0, 0.6)',
            border: '1px solid #444',
            borderRadius: '10px',
            padding: '20px',
            marginBottom: '20px'
        },
        ar_log_stat_grid: {
            display: 'grid',
            gridTemplateColumns: 'repeat(2, 1fr)',
            gap: '20px',
            marginTop: '20px'
        },
        ar_log_stat_item: {
            background: 'rgba(255, 255, 255, 0.05)',
            padding: '15px',
            borderRadius: '8px',
            textAlign: 'center'
        },
        ar_stat_value: {
            fontSize: '24px',
            fontWeight: 'bold',
            color: '#ffd700'
        },
        ar_stat_label: {
            fontSize: '14px',
            color: '#ccc'
        },
        ar_recent_activity: {
            marginTop: '30px'
        },
        ar_activity_row: {
            padding: '10px',
            borderBottom: '1px solid #333',
            fontSize: '14px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexDirection: isRtl ? 'row' : 'row-reverse'
        },
        ar_activity_content: {
            flex: 1,
            textAlign: isRtl ? 'right' : 'left'
        },
        ar_activity_time: {
            color: '#888',
            fontSize: '12px',
            minWidth: '100px',
            textAlign: isRtl ? 'left' : 'right'
        },
        ar_icon: {
            fontSize: '18px',
            marginLeft: isRtl ? '10px' : '0',
            marginRight: isRtl ? '0' : '10px',
            width: '30px',
            textAlign: 'center'
        }
    };

    // Helper to get icon based on log type
    const getLogIcon = (type) => {
        switch(type) {
            case 'catch': return '🔴'; // Pokeball
            case 'battle': return '⚔️'; // Swords
            case 'level_up': return '⬆️'; // Up arrow
            case 'shop': return '🛒'; // Cart
            default: return '📝';
        }
    };

    // Helper to format date
    const formatDate = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleString(i18n.language === 'he' ? 'he-IL' : 'en-US');
    };

    return (
        <div className="ar_mainBody">
            <GoldSiverHeader previous="/home" title={t('Adventure Log')} />

            <section style={styles.ar_log_section}>
                <div className="container">
                    <div className="row">
                        <div className="col-md-4">
                            {/* Profile Summary */}
                            <div style={{...styles.ar_log_card, textAlign: 'center'}}>
                                <img src={`/images/character/${userRedux?.character || 'hero_male_1'}.png`} 
                                     onError={(e) => e.target.src = '/assets/images/playerProfile/character-image.png'}
                                     alt="Player" 
                                     style={{width: '120px', height: '120px', objectFit: 'contain', marginBottom: '20px'}} />
                                <h4>{userRedux?.username || 'Trainer'}</h4>
                                <p style={{color: '#aaa'}}>{t('Rank')}: {userRedux?.rank || 1}</p>
                                <hr style={{borderColor: '#444'}} />
                                <div>{t('Region')}: {userRedux?.region || 'Kanto'}</div>
                                {userRedux?.signup_date && <div>{t('Joined')}: {new Date(userRedux.signup_date).toLocaleDateString()}</div>}
                                
                                {stats && (
                                    <div style={{marginTop: '15px'}}>
                                        <div style={{color: '#aaa', fontSize: '14px'}}>{t('Time Played')}</div>
                                        <div style={{color: '#ffd700', fontWeight: 'bold', fontSize: '16px'}}>
                                            {stats.play_time_days} {t('days')}, {stats.play_time_hours} {t('hours')}, {stats.play_time_minutes} {t('minutes')}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className="col-md-8">
                            {/* Statistics (from user object) */}
                            <div style={styles.ar_log_card}>
                                <h4>{t('Statistics')}</h4>
                                <div style={styles.ar_log_stat_grid}>
                                    <div style={styles.ar_log_stat_item}>
                                        <div style={styles.ar_stat_value}>{userStats.caught_count}</div>
                                        <div style={styles.ar_stat_label}>{t('Pokemon Caught')}</div>
                                    </div>
                                    <div style={styles.ar_log_stat_item}>
                                        <div style={styles.ar_stat_value}>{userStats.wins}</div>
                                        <div style={styles.ar_stat_label}>{t('Battles Won')}</div>
                                    </div>
                                    <div style={styles.ar_log_stat_item}>
                                        <div style={styles.ar_stat_value}>{userStats.silver.toLocaleString()}</div>
                                        <div style={styles.ar_stat_label}>{t('Silver Earned')}</div>
                                    </div>
                                    <div style={styles.ar_log_stat_item}>
                                        <div style={styles.ar_stat_value}>{userStats.badges_count}</div>
                                        <div style={styles.ar_stat_label}>{t('Badges')}</div>
                                    </div>
                                </div>
                            </div>

                            {/* Recent Activity Log */}
                            <div style={{...styles.ar_log_card, ...styles.ar_recent_activity}}>
                                <h4>{t('Recent Activity')}</h4>
                                {loading ? (
                                    <div className="text-center p-3">{t('Loading...')}</div>
                                ) : logs.length === 0 ? (
                                    <div className="text-center p-3 text-muted">{t('No activity yet')}</div>
                                ) : (
                                    logs.map(log => (
                                        <div key={log.id} style={styles.ar_activity_row}>
                                            <div style={{display: 'flex', alignItems: 'center', flex: 1}}>
                                                <span style={styles.ar_icon}>{getLogIcon(log.type)}</span>
                                                <span style={styles.ar_activity_content}>
                                                    {t(log.message_key, log.params || {})}
                                                </span>
                                            </div>
                                            <span style={styles.ar_activity_time}>{formatDate(log.created_at)}</span>
                                        </div>
                                    ))
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default AdventureLog;
